/**
 * User Activity Log Report.
 * @module advisor/components/reports/UserActivityLogReport
 *
 */

import React, {Component} from 'react';
import debug from 'debug';
import {connect} from 'react-redux';
import Table from '../../../shared/components/Table';
import axiosInstance from '../../../shared/utils/axios';
import {Button, DatePicker, DescriptiveText, Dropdown, LoadingSpinner, RadioSet, Row, SectionTitle} from '../../../shared';
import {_openModal} from '../../../shared/reducers/modals';
import moment from 'moment';
import lazy from 'lazy.js';
import {_getPeriods, _getUnreadReports, isEntitled} from '../../../shared/store';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import {calculateFixedStartDate} from '../../../shared/utils/utils';

const log = debug('circleblack:report:userActivityLogReport');

// TODO Test User Activity Log for some users and using select all pages.
/**
 * User Activity Log Report component.
 *
 * @name UserActivityLogReport
 * @requires module:shared/components/Button
 * @requires module:shared/components/Table
 * @requires module:shared/components/SectionTitle
 * @requires module:shared/components/Dropdown
 *
 */
class UserActivityLogReport extends Component {
  constructor(props) {
    super(props);
    this.report = props.report;
    this.containerID = 0;
    this.counts = {};
    this.state = {
      firm: props.session.profile.firm,
      params: this.setupParams(this.report),
      roles: ['!investor'],
      timeType: 'FIXED',
      selectedAllUsers: false,
      selectedUsers: [],
      loading: true,
      step: 1,
      endDate: {},
      filters: {
        role: null,
      },
      today: moment().format('YYYY-MM-DD'),
    };
    log('constructor state', this.state);
  }

  setupParams = report => {
    log('setupParams');
    return lazy(report.parameters).indexBy('name').toObject();
  };

  initTimePeriods = () => {
    const {firm} = this.state;
    const minDate = moment(firm.createdAt, 'YYYY-MM-DD').format('YYYY-MM-DD');
    const maxDate = this.state.today;
    const endDate = {value: maxDate};
    const periods = lazy(this.props.periods)
      .map(p => {
        if (p.id === 'MAX') {
          let diff = moment(p.end).diff(minDate, 'd');
          return {...p, start: minDate, days: diff < 0 ? 0 : diff};
        }
        return p;
      })
      .filter((period) => !!moment(minDate).isSameOrBefore(moment(period.start)))
      .sortBy('days')
      .toArray();
    const timePeriod = periods[0];
    const params = {...this.state.params};
    params.startDate.value = moment().subtract(timePeriod.days, 'd').format('YYYY-MM-DD');
    params.endDate.value = this.state.today;
    this.setState({timePeriod, params, minDate, maxDate, periods, endDate});
    log('this.props.session', this.props.session);
  };

  changeTimePeriod = (name, p) => {
    const params = this.state.params;
    params.startDate.value = calculateFixedStartDate(this.state.minDate, this.state.params.endDate.value, p.value);
    this.setState({
      timePeriod: p.value,
      params,
    });
  };

  gatherRoles = () => axiosInstance.get(`/firms/${this.state.firm.id}/roles`, {params: {role: '!investor', sort: '+role'}})
    .then((res) => this.setState({...this.state, roles: res.data}));

  componentDidMount() {
    Promise.join(
      this.props.periods.length ? Promise.resolve().then(() => {
        this.initTimePeriods();
        this.setState({loading: false});
      })
        : this.props.getPeriods(),
      this.gatherRoles()
    );
  }

  componentDidUpdate(prevProps) {
    if (this.props.periods.length !== prevProps.periods.length) {
      this.initTimePeriods();
      this.setState({loading: false});
    }
  }

  canGenerate = (params) => !!(params.selectedAllUsers || params.selectedUsers) && params.firmID;

  generateReport = () => {
    if (!isEntitled([{group: 'compliance', feature: 'core', access: 'r'}])) {
      return;
    }
    if (this.state.params.startDate.value < this.state.minDate) {
      return this.props.openModal({
        type: 'MessageModal',
        title: 'Invalid Period',
        body: 'Reports can not be generated for the time period you have selected. Please try another period.',
      });
    }
    if (moment(this.state.params.endDate.value, 'YYYY-MM-DD').isBefore(this.state.params.startDate.value, 'YYYY-MM-DD')) {
      return this.props.openModal({
        type: 'MessageModal',
        title: 'Invalid Start and End Date.',
        body: 'Cannot generate report for an end date before the start date.',
      });
    }

    const {params} = this.state;
    this.setState({
      params: {
        ...params,
        firmID: {...params.firmID, value: this.state.firm.id},
        userIDs: this.state.selectedUsers === 'ALL' ? undefined : {
          ...params.userIDs,
          value: this.state.selectedUsers.map((user) => Number(user.id)),
        },
      },
    }, () => {
      log('UserIDs', this.state.params.userIDs);
      return axiosInstance.post(
        '/reports/' + this.report.name,
        {parameters: Object.values(this.state.params).filter(p => !!p).map((param) => ({id: param.id, value: param.value}))}
      )
        .then(() => {
          this.setState({
            message: 'Success! Your report is being generated and you will receive a notification when it is ready.',
            step: this.state.step + 1,
          });

          this.props.getUnreadReports(
            this.props.unreadReportsTimeoutID, this.props.unreadReports, this.props.reportPageVisitTimestamp, 30000
          );
        })
        .catch(err => this.props.openModal({
          type: 'MessageModal',
          title: err.response && err.response.data && err.response.data.error && err.response.data.error.title !== undefined
            ? err.response.data.error.title
            : 'Report Couldn\'t be created',
          body: (err.response && err.response.data && err.response.data.error && err.response.data.error.msg) || err.message,
        }));
    });
  };

  userDataManager = {
    gatherData: (inputValue) => axiosInstance.get(
      `/firms/${this.state.firm.id}/users`,
      {params: {...inputValue, roleDisplayName: this.state.filters.role || this.state.roles}}
    )
      .then(res => res.data)
      .catch(function error(err) {
        log('error', err);
        throw err;
      }),
    setSelectedRows: rows => {
      this.setState({selectedUsers: rows});
    },
  };

  updateValue = (name, option) => {
    let filters = {...this.state.filters};
    filters[name] = option && option.value || null;
    log('newState', filters);
    this.setState({filters: filters}, () => {
      const dataManager = this.userDataManager;
      dataManager.refresh();
    });
  };

  updateParams = (name, value) => {
    const params = this.state.params;
    params[name].value = value;
    this.setState({params});
  };

  render() {
    log('rendering', this.props);
    if (this.state.loading) {
      return <LoadingSpinner/>;
    }
    const {
      params,
      step,
      timeType,
      timePeriod,
    } = this.state;
    log('params', params);

    const renderStep = () => {
      log('renderStep', 'params', this.state.params);
      switch (step) {
        case 1:
          return <div>
            <DescriptiveText>The User Activity Log Report displays a list of specific actions taken by a user in the CircleBlack
              Advisor platform within a specified period of time. The report can be generated for one or multiple Advisor users
              within your firm. This includes other users like Assistants if your firm has them setup.</DescriptiveText>
            <div>
              <SectionTitle title={'Time Period'}/>
              <div>
                <RadioSet dataPoint={{name: 'timeType', value: timeType}}
                          onChange={(name, value) => this.setState({
                            timeType: value,
                            params: {
                              ...params,
                              startDate: {
                                ...params.startDate,
                                value: value === 'FIXED'
                                  ? moment().subtract(timePeriod.days, 'd').format('YYYY-MM-DD')
                                  : params.startDate.value,
                              },
                            },
                          })}
                          options={[
                            {value: 'FIXED', label: 'Fixed Time Periods'},
                            {value: 'CUSTOM', label: 'Custom Time Periods'},
                          ]}
                          optionWidth='200px'
                          orientation='row'
                          readOnly={false}
                          width='100%'
                />
              </div>
              {timeType === 'CUSTOM' && <Row justifySubRow={'left'}>
                <DatePicker title={'Start Date'}
                            width={'fit-content'}
                            dataPoint={{name: 'startDate', value: params.startDate.value}}
                            minDate={this.state.minDate}
                            maxDate={this.state.today}
                            onChange={this.updateParams}/>
                <DatePicker title={'End Date'}
                            width={'fit-content'}
                            dataPoint={{name: 'endDate', value: params.endDate.value}}
                            minDate={this.state.minDate}
                            maxDate={this.state.today}
                            defaultDate={this.state.today}
                            onChange={this.updateParams}/>
              </Row>}
              {timeType === 'FIXED' && <Row justifySubRow={'left'}>
                <DatePicker title={'As Of Date'}
                            width={'fit-content'}
                            dataPoint={{name: 'endDate', value: params.endDate.value}}
                            minDate={this.state.minDate}
                            maxDate={this.state.today}
                            defaultDate={this.state.today}
                            onChange={this.updateParams}/>
                {<Dropdown onChange={this.changeTimePeriod}
                           title={'Time Period'}
                           options={this.state.periods.map(period => ({
                             label: period.name,
                             value: period,
                           }))}
                           isSearchable={true}
                           dataPoint={{
                             name: 'timePeriod',
                             value: {label: timePeriod.name, value: timePeriod},
                           }}
                           placeholder={'Select a Period'}/>}
              </Row>}
              <SectionTitle title={'Users'}/>
              <Table
                dataManager={this.userDataManager}
                columns={[
                  {
                    title: 'User',
                    dataPoint: 'name',
                    type: 'text',
                    minWidth: 50,
                    isSortable: true,
                  },
                  {
                    title: 'Role',
                    dataPoint: 'roleDisplayName',
                    type: 'text',
                    minWidth: 50,
                    isSortable: true,
                  },
                ]}
                searches={[{title: 'User', name: 'name', isFuzzy: true}]}
                defaultSort={{column: 'name', desc: false}}
                select={{
                  type: 'all',
                  max: 50,
                }}
                rowIdentifier={'id'}
                filterValues={this.state.filters}
                filters={[
                  <Dropdown
                    key={'filterByRole'}
                    title='Role'
                    type='dropdown'
                    placeholder='Filter by Role'
                    dataPoint={{
                      name: 'role',
                      value: this.state.filters.role && {label: this.state.filters.role, value: this.state.filters.role},
                    }}
                    onChange={this.updateValue}
                    options={this.state.roles.sort().map(Dropdown.buildSimpleOption)}
                    isClearable
                  />,
                ]}
                height={'500px'}/>
              <Button importance={'primary'}
                      size={'lg'}
                      label={'Generate'}
                      disabled={!this.canGenerate({
                        selectedAllUsers: this.state.selectedAllUsers,
                        selectedUsers: this.state.selectedUsers.length,
                        firmID: this.state.firm.id,
                      })}
                      action={this.generateReport}/>
            </div>
          </div>;
        case 2:
          return <DescriptiveText>{this.state.message}</DescriptiveText>;
        default:
          return <div>Something went wrong</div>; // TODO Handle This
      }
    };
    // TODO User Activity Log Report to use Steps Component even though there is only 1 step and a success message?
    // TODO Do something as a loading screen as a step transaction optionally
    return renderStep(step); // ETODO Update User Activity Log to use steps component.
  }
}

const mapStateToProps = state => ({
  session: state.session,
  periods: state.periods,
  unreadReportsTimeoutID: state.badges.unreadReportsTimeoutID,
  unreadReports: state.badges.unreadReports,
  reportPageVisitTimestamp: state.badges.reportPageVisitTimestamp,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  getPeriods: () => dispatch(_getPeriods()),
  getUnreadReports: (timeoutID, unread, timestamp, interval) => dispatch(_getUnreadReports(timeoutID, unread, timestamp, interval)),
});

UserActivityLogReport.propTypes = {
  /**
   * The Report to be generated
   */
  report: PropTypes.shape({}).isRequired,
  /**
   * Is from Models Reducer
   */
  openModal: PropTypes.func.isRequired,
  /**
   * Is from Periods Reducer
   */
  getPeriods: PropTypes.func.isRequired,
  /**
   * Is from Periods Reducer
   */
  periods: PropTypes.array.isRequired,
  /**
   /**
   * Is Session Reducer
   */
  session: PropTypes.shape({
    profile: PropTypes.shape({
      firm: PropTypes.shape({
        id: PropTypes.number.isRequired,
        createdAt: PropTypes.string.isRequired,
      }),
    }),
  }).isRequired,
  /**
   * Are from badges reducer
   */
  getUnreadReports: PropTypes.func,
  unreadReportsTimeoutID: PropTypes.object,
  unreadReports: PropTypes.object,
  reportPageVisitTimestamp: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserActivityLogReport);
